import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { BarChart3, Clock, Star, Wrench } from 'lucide-react';
import { formatDate } from '@/lib/utils';

export type OficinaMetricas = {
  totalAgendamentos: number;
  porJanela: Record<string, { total: number; concluidos: number }>;
  porClassificacao: Record<string, number>;
  porAvaliacao: { RUIM: number; BOM: number; OTIMO: number };
  npsScore: number | null;
  noShow: number;
  tempoMedioMinutos: number | null;
};

const CLASSIFICACAO_CHART: Record<string, { label: string; fill: string }> = {
  EXPRESSO: { label: 'Expresso', fill: '#22c55e' },
  INTERMEDIARIO: { label: 'Intermediário', fill: '#eab308' },
  TECNICO: { label: 'Técnico', fill: '#ef4444' },
};

const AVALIACAO_CHART: { key: keyof OficinaMetricas['porAvaliacao']; label: string; fill: string }[] = [
  { key: 'RUIM', label: 'Ruim', fill: '#ef4444' },
  { key: 'BOM', label: 'Bom', fill: '#f59e0b' },
  { key: 'OTIMO', label: 'Ótimo', fill: '#10b981' },
];

function sortJanelaHora(a: string, b: string): number {
  const pa = a.split(':').map(Number);
  const pb = b.split(':').map(Number);
  return (pa[0] ?? 0) * 60 + (pa[1] ?? 0) - ((pb[0] ?? 0) * 60 + (pb[1] ?? 0));
}

function ChartEmpty({ message }: { message: string }) {
  return (
    <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

type TooltipPayload = { name?: string; value?: number; color?: string };

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md">
      {label ? <p className="font-medium mb-1">{label}</p> : null}
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: <span className="font-semibold">{p.value ?? 0}</span>
        </p>
      ))}
    </div>
  );
}

function AvaliacaoPieLabel(props: {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
  name?: string;
}) {
  const { cx = 0, cy = 0, midAngle = 0, innerRadius = 0, outerRadius = 0, percent = 0, name = '' } =
    props;
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="currentColor" textAnchor="middle" dominantBaseline="central" className="text-xs fill-foreground">
      {name} {Math.round(percent * 100)}%
    </text>
  );
}

type Props = {
  metricas: OficinaMetricas;
  dataFrom: string;
  dataTo: string;
};

export const ZehMotocaIndicadoresPanel: React.FC<Props> = ({ metricas, dataFrom, dataTo }) => {
  const avaliacaoChartData = useMemo(() => {
    const pa = metricas.porAvaliacao ?? { RUIM: 0, BOM: 0, OTIMO: 0 };
    return AVALIACAO_CHART.map(({ key, label, fill }) => ({
      name: label,
      value: pa[key] ?? 0,
      fill,
    })).filter((d) => d.value > 0);
  }, [metricas.porAvaliacao]);

  const avaliacaoTotal = useMemo(
    () => avaliacaoChartData.reduce((s, d) => s + d.value, 0),
    [avaliacaoChartData]
  );

  const janelaChartData = useMemo(() => {
    const entries = Object.entries(metricas.porJanela ?? {});
    return entries
      .sort(([a], [b]) => sortJanelaHora(a, b))
      .map(([hora, v]) => ({
        hora: hora.replace(':00', 'h'),
        total: v.total,
        concluidos: v.concluidos,
      }));
  }, [metricas.porJanela]);

  const classificacaoChartData = useMemo(() => {
    const entries = Object.entries(metricas.porClassificacao ?? {});
    return entries
      .map(([key, total]) => {
        const meta = CLASSIFICACAO_CHART[key] ?? { label: key, fill: '#64748b' };
        return { name: meta.label, total, fill: meta.fill };
      })
      .sort((a, b) => b.total - a.total);
  }, [metricas.porClassificacao]);

  const conclusaoPct =
    metricas.totalAgendamentos > 0
      ? Math.round(
          (janelaChartData.reduce((s, j) => s + j.concluidos, 0) / metricas.totalAgendamentos) * 100
        )
      : null;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Período: {formatDate(dataFrom)} — {formatDate(dataTo)}
        {conclusaoPct != null ? (
          <span className="ml-2">· Taxa de conclusão (por janela): {conclusaoPct}%</span>
        ) : null}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Total agendamentos</p>
            <p className="text-2xl font-bold tabular-nums">{metricas.totalAgendamentos}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">NPS (Ótimo − Ruim)</p>
            <p className="text-2xl font-bold tabular-nums">{metricas.npsScore ?? '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">Tempo médio (min)</p>
            <p className="text-2xl font-bold tabular-nums">{metricas.tempoMedioMinutos ?? '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">No-show</p>
            <p className="text-2xl font-bold tabular-nums">{metricas.noShow}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Agendamentos por janela
            </CardTitle>
          </CardHeader>
          <CardContent>
            {janelaChartData.length === 0 ? (
              <ChartEmpty message="Sem agendamentos por janela no período." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={janelaChartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="hora" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} width={32} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="concluidos" name="Concluídos" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              Por classificação do serviço
            </CardTitle>
          </CardHeader>
          <CardContent>
            {classificacaoChartData.length === 0 ? (
              <ChartEmpty message="Sem classificação registrada no período." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  layout="vertical"
                  data={classificacaoChartData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="total" name="Agendamentos" radius={[0, 4, 4, 0]}>
                    {classificacaoChartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Star className="h-4 w-4 text-muted-foreground" />
              Avaliações dos clientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {avaliacaoTotal === 0 ? (
              <ChartEmpty message="Nenhuma avaliação no período." />
            ) : (
              <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_200px] items-center">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={avaliacaoChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={56}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={AvaliacaoPieLabel}
                    >
                      {avaliacaoChartData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="space-y-3 text-sm">
                  {AVALIACAO_CHART.map(({ key, label, fill }) => {
                    const n = metricas.porAvaliacao?.[key] ?? 0;
                    const pct = avaliacaoTotal > 0 ? Math.round((n / avaliacaoTotal) * 100) : 0;
                    return (
                      <li key={key} className="flex items-center justify-between gap-4">
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block h-3 w-3 rounded-full shrink-0"
                            style={{ backgroundColor: fill }}
                          />
                          {label}
                        </span>
                        <span className="font-semibold tabular-nums">
                          {n}{' '}
                          <span className="text-muted-foreground font-normal">({pct}%)</span>
                        </span>
                      </li>
                    );
                  })}
                  <li className="pt-2 border-t text-xs text-muted-foreground flex items-center gap-2">
                    <BarChart3 className="h-3.5 w-3.5" />
                    {avaliacaoTotal} avaliação(ões) · NPS {metricas.npsScore ?? '—'}
                  </li>
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
